const META_CONTEXT_ID = 'hd-meta-pixel-context';
const CART_ADD_PATH_PATTERN = /\/cart\/add(?:\.js)?(?:\?|$)/;
const CART_PATH = '/cart.js';
const EVENT_DEDUPE_MS = 1800;

class HDMetaEvents {
  constructor() {
    this.context = this.readContext();
    this.sentEvents = new Map();
    this.cartSnapshot = null;

    if (!this.context?.pixelId) return;

    this.trackPageEvents();
    this.patchFetch();
    this.bindCartUpdateTracking();
    this.bindContactTracking();
    this.primeCartSnapshot();
  }

  readContext() {
    const node = document.getElementById(META_CONTEXT_ID);
    if (!node?.textContent) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[HD Meta Pixel] Could not parse context.', error);
      return null;
    }
  }

  trackPageEvents() {
    if (this.context.pageType === 'product' && this.context.product) {
      const product = this.context.product;
      if (!this.isCurrentProductPage(product)) return;

      const contentId = this.contentIdForProduct(product);

      this.track('ViewContent', {
        content_type: 'product',
        content_ids: this.cleanIds([contentId]),
        contents: [
          {
            id: String(contentId || ''),
            quantity: 1,
            item_price: this.money(product.price),
          },
        ],
        content_name: product.title,
        content_category: product.type || 'T-Shirts',
        content_url: this.absoluteUrl(product.url || window.location.href),
        currency: this.currency(),
        value: this.money(product.price),
        hd_page_url: window.location.href,
        hd_context_path: this.stringOrEmpty(this.context.path),
        hd_product_id: this.stringOrEmpty(product.id),
        hd_variant_id: this.stringOrEmpty(product.variantId),
        hd_catalog_id: this.stringOrEmpty(product.catalogId),
        hd_sku: this.stringOrEmpty(product.sku),
      });
    }

    if (this.context.pageType === 'search' && this.context.searchTerm) {
      this.track('Search', {
        search_string: this.context.searchTerm,
        content_type: 'product',
      });
    }
  }

  patchFetch() {
    if (window.__hdMetaPixelFetchPatched || typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);
    window.__hdMetaPixelFetchPatched = true;

    window.fetch = async (...args) => {
      const requestUrl = this.requestUrl(args[0]);
      const isCartAdd = CART_ADD_PATH_PATTERN.test(requestUrl);
      const response = await originalFetch(...args);

      if (isCartAdd && response.ok) {
        this.handleCartAddResponse(response.clone()).catch((error) => {
          console.warn('[HD Meta Pixel] AddToCart fetch tracking failed.', error);
        });
      }

      return response;
    };
  }

  async handleCartAddResponse(response) {
    let data;

    try {
      data = await response.json();
    } catch {
      return;
    }

    if (!data || data.status) return;

    const items = this.normalizeCartAddItems(data);
    if (!items.length) return;

    this.trackCartItems('AddToCart', items);
    this.refreshCartSnapshot();
  }

  normalizeCartAddItems(data) {
    if (Array.isArray(data.items)) return data.items;
    if (data.variant_id || data.product_id || data.id) return [data];
    return [];
  }

  bindCartUpdateTracking() {
    document.addEventListener(
      'cart:update',
      (event) => {
        this.handleCartUpdateEvent(event).catch((error) => {
          console.warn('[HD Meta Pixel] AddToCart cart event tracking failed.', error);
        });
      },
      true
    );
  }

  async handleCartUpdateEvent(event) {
    const detail = event?.detail || {};
    const data = detail.data || {};

    if (data.didError) return;
    if (!this.isCartAddLikeEvent(detail)) return;

    const variantId = data.variantId || detail.sourceId;
    if (!variantId || !/^\d+$/.test(String(variantId))) return;

    const quantity = Number(data.itemCount || data.quantity || 1);
    const item = await this.findCartItemForVariant(variantId);
    if (!item) return;

    this.trackCartItems('AddToCart', [
      {
        ...item,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      },
    ]);
  }

  isCartAddLikeEvent(detail) {
    const data = detail?.data || {};
    const source = String(data.source || '').toLowerCase();
    const hasAddedVariant = Boolean(data.variantId || detail?.sourceId);

    if (!hasAddedVariant) return false;
    if (source.includes('product-form') || source.includes('quick-add')) return true;

    return false;
  }

  async findCartItemForVariant(variantId) {
    let cart = this.cartSnapshot;
    let item = this.cartItemForVariant(cart, variantId);
    if (item) return item;

    await this.refreshCartSnapshot();
    cart = this.cartSnapshot;
    item = this.cartItemForVariant(cart, variantId);
    return item;
  }

  cartItemForVariant(cart, variantId) {
    if (!cart?.items?.length) return null;

    return cart.items.find((item) => String(item.variant_id || item.id) === String(variantId)) || null;
  }

  trackCartItems(eventName, items) {
    const normalized = items
      .map((item) => this.metaItemFromCartItem(item))
      .filter((item) => item.id);

    if (!normalized.length) return;

    const value = items.reduce((total, item) => total + this.lineValue(item), 0);

    this.track(eventName, {
      content_type: 'product',
      content_ids: this.cleanIds(normalized.map((item) => item.id)),
      contents: normalized.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        item_price: item.item_price,
      })),
      content_name: items.map((item) => item.product_title || item.title).filter(Boolean).join(', '),
      currency: this.currency(),
      value: this.money(value),
      hd_product_ids: this.cleanIds(items.map((item) => item.product_id || item.productId)),
      hd_variant_ids: this.cleanIds(items.map((item) => item.variant_id || item.variantId || item.id)),
      hd_skus: this.cleanIds(items.map((item) => item.sku)),
    });
  }

  metaItemFromCartItem(item) {
    const variantId = item.variant_id || item.variantId || item.id;
    const productId = item.product_id || item.productId;
    const id = this.catalogContentId(productId, variantId) || this.stringOrEmpty(variantId || item.sku);

    return {
      id,
      quantity: Number(item.quantity || 1),
      item_price: this.money(item.final_price ?? item.price),
    };
  }

  bindCheckoutTracking() {
    const onCheckoutIntent = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkoutTarget = target.closest(
        'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .cart__checkout-button, shopify-accelerated-checkout-cart'
      );
      if (!checkoutTarget) return;

      this.trackInitiateCheckout();
    };

    document.addEventListener('click', onCheckoutIntent, true);
    document.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter instanceof Element ? event.submitter : null;
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        if (submitter?.matches('[name="checkout"]') || form?.querySelector('[name="checkout"]')) {
          this.trackInitiateCheckout();
        }
      },
      true
    );
  }

  trackInitiateCheckout() {
    const cart = this.cartSnapshot;
    const params = {
      content_type: 'product',
      currency: this.currency(),
    };

    if (cart?.items?.length) {
      const normalized = cart.items.map((item) => this.metaItemFromCartItem(item)).filter((item) => item.id);

      params.content_ids = this.cleanIds(normalized.map((item) => item.id));
      params.contents = normalized.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        item_price: item.item_price,
      }));
      params.num_items = Number(cart.item_count || cart.items.reduce((total, item) => total + Number(item.quantity || 0), 0));
      params.value = this.money(cart.total_price);
      params.hd_product_ids = this.cleanIds(cart.items.map((item) => item.product_id));
      params.hd_variant_ids = this.cleanIds(cart.items.map((item) => item.variant_id || item.id));
      params.hd_skus = this.cleanIds(cart.items.map((item) => item.sku));
    }

    this.track('InitiateCheckout', params);
  }

  bindContactTracking() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest('a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;

        const href = link.href || '';
        if (!/^(mailto:|tel:)|wa\.me|api\.whatsapp\.com|web\.whatsapp\.com/i.test(href)) return;

        this.track('Contact', {
          content_name: link.textContent?.trim() || href,
        });
      },
      true
    );
  }

  primeCartSnapshot() {
    const shouldPrime =
      document.body?.classList.contains('template-cart') ||
      document.querySelector('cart-drawer-component, .cart__checkout-button');

    if (shouldPrime) this.refreshCartSnapshot();
  }

  async refreshCartSnapshot() {
    try {
      const response = await fetch(CART_PATH, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      this.cartSnapshot = await response.json();
    } catch {
      this.cartSnapshot = null;
    }
  }

  requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.pathname + input.search;
    if (input instanceof Request) {
      try {
        const url = new URL(input.url, window.location.origin);
        return url.pathname + url.search;
      } catch {
        return input.url;
      }
    }
    return '';
  }

  isCurrentProductPage(product) {
    const currentPath = this.normalizedPath(window.location.pathname);
    const contextPath = this.normalizedPath(this.context.path);
    const productPath = this.normalizedPath(this.pathFromUrl(product.url));

    if (!currentPath.includes('/products/')) return false;
    if (!contextPath.includes('/products/') && !productPath.includes('/products/')) return false;
    if (productPath && productPath !== currentPath) return false;
    if (contextPath && contextPath !== currentPath) return false;

    return true;
  }

  pathFromUrl(url) {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return String(url).split('?')[0] || '';
    }
  }

  normalizedPath(path) {
    const cleanPath = String(path || '').split('?')[0].replace(/\/+$/, '');
    return cleanPath || '/';
  }

  absoluteUrl(url) {
    if (!url) return window.location.href;

    try {
      return new URL(url, window.location.origin).href;
    } catch {
      return window.location.href;
    }
  }

  contentIdForProduct(product) {
    return (
      this.catalogContentId(product.id, product.variantId) ||
      this.stringOrEmpty(product.variantId || product.id || product.sku)
    );
  }

  catalogContentId(productId, variantId) {
    return this.stringOrEmpty(variantId || productId);
  }

  countryCode() {
    return String(this.context.countryCode || 'IN').trim().toUpperCase() || 'IN';
  }

  lineValue(item) {
    if (typeof item.line_price === 'number') return item.line_price;
    if (typeof item.final_line_price === 'number') return item.final_line_price;

    const price = Number(item.final_price ?? item.price ?? 0);
    const quantity = Number(item.quantity || 1);
    return price * quantity;
  }

  money(value) {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 0;

    return numeric > 999 ? Number((numeric / 100).toFixed(2)) : Number(numeric.toFixed(2));
  }

  currency() {
    return this.context.currency || 'INR';
  }

  cleanIds(ids) {
    return [
      ...new Set(
        ids
          .filter((id) => id !== null && id !== undefined && String(id).trim() !== '')
          .map((id) => String(id).trim())
      ),
    ];
  }

  stringOrEmpty(value) {
    if (value === null || value === undefined) return '';

    return String(value).trim();
  }

  track(eventName, params = {}) {
    const key = `${eventName}:${JSON.stringify(params.content_ids || params.search_string || params.content_name || this.context.path || '')}`;
    const now = Date.now();
    const lastSent = this.sentEvents.get(key) || 0;

    if (now - lastSent < EVENT_DEDUPE_MS) return;
    this.sentEvents.set(key, now);

    const send = () => {
      if (typeof window.fbq === 'function') {
        window.fbq('trackSingle', this.context.pixelId, eventName, params);
        return true;
      }

      return false;
    };

    if (send()) return;

    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      if (send() || attempts > 20) window.clearInterval(retry);
    }, 250);
  }
}

new HDMetaEvents();
